/* eslint-disable jsx-a11y/no-autofocus */

import React, { memo, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { themeGet, SpaceProps, WidthProps } from 'styled-system'
import { Button, Flex, Tooltip, getPaletteColor } from 'pcln-design-system'
import { User as UserIcon } from 'pcln-icons'
import Popover from 'pcln-popover'
import TravelerSelectionContent from '../TravelerSelectionContent'
import useTravelerSelection from '../useTravelerSelection'
import { DEFAULT_MAX_TRAVELERS } from '../constants'
import { ITravelerSelection } from '../types'

const Container = styled(Flex)`
  position: relative;
  height: ${(props) => props.height}px;

  & > :last-child {
    width: 100%;
    ${(props) => (props.isOpen ? 'z-index: 1502' : '')};
  }

  @media (max-width: 640px) {
    position: ${(props) => (props.isOpen ? 'fixed' : 'relative')};
    ${(props) => (props.isOpen ? 'z-index: 1502' : '')};
    ${(props) => (props.isOpen ? 'margin-top: 0' : '')};
    top: 0;
    left: 0;
    right: 0;

    ${(props) =>
      props.isOpen &&
      `
      &::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${getPaletteColor('background.lightest')(props)};
        z-index: 1501;
      }
    `}
  }
`

const StyledPopover = styled(Popover)`
  position: relative;
  max-width: 325px;
  padding: ${themeGet('space.1')}px !important;

  @media (max-width: 640px) {
    top: ${(props) => props.height}px !important;
    left: 0px !important;
    max-width: 100% !important;
    padding: 0 !important;
    transform: none !important;
  }
`

const PopoverButton = styled(Button)`
  display: flex;
  align-items: center;
  height: 100%;
  font-weight: normal;
  border-width: 1px;
  border-style: solid;
  border-color: ${(props) =>
    props.hasError ? getPaletteColor('error.base')(props) : getPaletteColor('border.base')(props)};
  outline: none;

  &:focus {
    border: 1px solid ${getPaletteColor('primary.base')};
    box-shadow: 0 0 0 2px ${getPaletteColor('primary.base')};
  }
  border-radius: ${(props) => props.theme.borderRadii[props.borderRadius]};

  &&& {
    font-size: ${themeGet('fontSizes.2')}px;

    ${themeGet('mediaQueries.lg')} {
      font-size: ${themeGet('fontSizes.1')}px;
    }
  }
`

const StyledTooltip = styled(Tooltip)`
  top: 20px;
  z-index: 9;

  &::after {
    &:focus {
      outline: none;
    }
  }
`

const ShadowEffect = styled('div')`
  z-index: 10;
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(51, 51, 51, 0.65);
  pointer-events: none;
`

/** @public */
const TravelerSelection = ({
  ageSelectEnabled,
  borderRadius,
  className,
  disclaimer,
  hasShadowEffect,
  height = 42,
  initialValues,
  isChildAgeSeti,
  isInternationalFlight,
  isTravelerSelectionDisabled,
  isWhiteLabel,
  maxAdults,
  maxChildren,
  maxTravelers = DEFAULT_MAX_TRAVELERS,
  roomsEnabled,
  fireChildCountImpression = () => {},
  onChange,
  shouldRemoveGuestsPerRoomRestriction,
  trapFocus = true,
  subLabelMap,
  lapInfantLabel,
  ...props
}: ITravelerSelection & SpaceProps & WidthProps) => {
  const useTravelerSelectionProps = useMemo(
    () => ({
      initialValues: {
        rooms: roomsEnabled ? initialValues?.rooms : 0,
        ...initialValues,
      },
      ageSelectEnabled: !!ageSelectEnabled,
      isInternationalFlight: !!isInternationalFlight,
      isWhiteLabel,
      maxTravelers,
      shouldRemoveGuestsPerRoomRestriction,
    }),
    [initialValues]
  )

  const [isOpen, setOpen] = useState(false)
  const state = useTravelerSelection(useTravelerSelectionProps)

  useEffect(() => {
    const rooms = roomsEnabled ? useTravelerSelectionProps.initialValues.rooms : 0

    state.setRooms(rooms)
    if (onChange) {
      onChange({ ...state, rooms })
    }
  }, [roomsEnabled])

  if (state.children && isOpen && fireChildCountImpression) {
    fireChildCountImpression()
  }

  function onOpen() {
    setOpen(true)
  }
  function onClose() {
    setOpen(false)
    /* istanbul ignore else */
    if (onChange) {
      onChange(state)
    }
  }

  /* eslint-disable react/prop-types */
  function PopoverContent({ handleClose }) {
    return (
      <TravelerSelectionContent
        ageSelectEnabled={ageSelectEnabled}
        autoFocus={trapFocus}
        borderRadius={borderRadius}
        disclaimer={disclaimer}
        isChildAgeSeti={isChildAgeSeti}
        maxAdults={maxAdults}
        maxChildren={maxChildren}
        roomsEnabled={roomsEnabled}
        state={state}
        handleClose={handleClose}
        subLabelMap={subLabelMap}
        lapInfantLabel={lapInfantLabel}
      />
    )
  }

  return (
    <Container className={className} isOpen={isOpen} width={1} height={height} {...props}>
      {isOpen && hasShadowEffect && <ShadowEffect data-testid='shadow-effect' />}
      <StyledPopover
        idx='traveler-selection-popover'
        hideArrow
        trapFocus={trapFocus}
        ariaLabel='Select traveler information'
        width={325}
        height={height}
        zIndex={1502}
        placement='bottom'
        renderContent={PopoverContent}
        onOpen={onOpen}
        onClose={onClose}
      >
        <PopoverButton
          isOpen={isOpen}
          hasError={state.errorMessage && !isOpen}
          color='background.lightest'
          width={1}
          p={2}
          disabled={isTravelerSelectionDisabled}
          borderRadius={borderRadius}
        >
          <Flex alignItems='center' width={1}>
            <UserIcon color='primary' mr={2} />
            {state.label}
          </Flex>
          {!isOpen && state.errorMessage && (
            <StyledTooltip width={250} color='error' right bottom>
              {state.errorMessage}
            </StyledTooltip>
          )}
        </PopoverButton>
      </StyledPopover>
    </Container>
  )
}

export default memo(TravelerSelection)
